import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Bell, Settings, User, ChevronDown, FileText, ScrollText, Crosshair, Target, ClipboardCheck, ShieldCheck, Plug } from 'lucide-react';
import { useMockUser, getRoleDisplayName, getRoleColor } from '@/hooks/useMockUser';
import { cn } from '@/lib/utils';
import { AIPromptInput } from '@/components/AIAssistant';

// Import navigation sections from Sidebar (we'll export these)
interface NavigationSection {
  id: string;
  title: string;
  icon: any;
  color: string;
  bgColor: string;
  headerBg: string;
  items: any[];
}

interface HeaderProps {
  onAIPromptSubmit?: (prompt: string) => void;
}

export function Header({ onAIPromptSubmit }: HeaderProps = {}) {
  const { userProfile } = useMockUser();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const location = useLocation();

  const { user } = userProfile;

  // Determine active section based on current path
  const getActiveSection = () => {
    const currentPath = location.pathname;
    
    // Check admin paths first - return null so nothing is highlighted
    if (currentPath.startsWith('/admin/integrations') || currentPath.startsWith('/notifications') || 
        currentPath.startsWith('/reports') || currentPath.startsWith('/audit') || 
        currentPath.startsWith('/settings')) return null;
    
    // Check Assess paths BEFORE Capture (to catch /capacity-skills/intelligence)
    if (currentPath.startsWith('/assessments') || currentPath.startsWith('/scenarios') || 
        currentPath.startsWith('/capacity-skills/intelligence') ||
        currentPath.startsWith('/integrations')) return 'assess';
    
    if (currentPath.startsWith('/capacity-skills') || currentPath.startsWith('/business-forecast') || 
        currentPath.startsWith('/processes') || currentPath.startsWith('/customers') || 
        currentPath.startsWith('/suppliers') || currentPath.startsWith('/risks')) return 'capture';
    if (currentPath.startsWith('/policies') || currentPath.startsWith('/risk-appetite')) return 'appetite';
    if (currentPath.startsWith('/controls') || currentPath.startsWith('/compliance') || 
        currentPath.startsWith('/actions') || currentPath.startsWith('/playbooks') || 
        currentPath.startsWith('/bcp') || currentPath.startsWith('/kpis') || 
        currentPath.startsWith('/alerts')) return 'mitigate';
    
    return null; // default - no highlight
  };

  const activeSection = getActiveSection();

  // Navigation sections with proper paths and more vibrant colors
  const navigationSections: NavigationSection[] = [
    { id: 'capture', title: 'Capture', icon: Crosshair, color: 'text-cyan-300', bgColor: 'bg-cyan-500/30', headerBg: '#0891b2', items: [{ path: '/capacity-skills' }] },
    { id: 'appetite', title: 'Define Appetite', icon: Target, color: 'text-purple-300', bgColor: 'bg-purple-500/30', headerBg: '#9333ea', items: [{ path: '/policies' }] },
    { id: 'assess', title: 'Assess', icon: ClipboardCheck, color: 'text-blue-300', bgColor: 'bg-blue-500/30', headerBg: '#2563eb', items: [{ path: '/assessments' }] },
    { id: 'mitigate', title: 'Mitigate', icon: ShieldCheck, color: 'text-green-300', bgColor: 'bg-green-500/30', headerBg: '#16a34a', items: [{ path: '/controls' }] },
  ];

  const currentSection = navigationSections.find(s => s.id === activeSection);
  const headerBg = currentSection?.headerBg || '#1E3A36';

  return (
    <header className="border-b border-black/20 transition-colors duration-300" style={{ backgroundColor: headerBg }}>
      <div className="px-6">
        <div className="flex h-16 items-center justify-between gap-4">
          {/* Left Side - Workflow Tabs */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {navigationSections.map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              
              return (
                <NavLink
                  key={section.id}
                  to={section.items[0]?.path || '#'}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 text-sm font-medium",
                    isActive 
                      ? `${section.bgColor} ${section.color} border-2 border-current/50 shadow-lg` 
                      : "text-white/80 hover:text-white hover:bg-white/20"
                  )}
                >
                  <Icon className="w-4 h-4" />
                  <span>{section.title}</span>
                </NavLink>
              );
            })}
          </div>

          {/* Center - AI Prompt Input */}
          <div className="flex-1 max-w-md mx-4">
            <AIPromptInput
              onSubmit={onAIPromptSubmit || (() => {})}
              placeholder="Ask Themis anything..."
              className="w-full"
            />
          </div>

          {/* Right Side - Admin Controls & User Profile */}
          <div className="flex items-center space-x-2 flex-shrink-0">
          </div>

          {/* Right Side - Admin Controls & User Profile */}
          <div className="flex items-center space-x-2">
            {/* Admin Controls */}
            <NavLink
              to="/notifications"
              className={({ isActive }) =>
                cn(
                  'p-2 text-white/80 hover:text-white transition-colors rounded-lg hover:bg-white/20',
                  isActive && 'text-cyan-300 bg-cyan-500/30 shadow-md'
                )
              }
              title="Notifications"
            >
              <Bell size={20} />
            </NavLink>

            <NavLink
              to="/admin/integrations"
              className={({ isActive }) =>
                cn(
                  'p-2 text-white/80 hover:text-white transition-colors rounded-lg hover:bg-white/20',
                  isActive && 'text-cyan-300 bg-cyan-500/30 shadow-md'
                )
              }
              title="Integrations"
            >
              <Plug size={20} />
            </NavLink>

            <NavLink
              to="/reports"
              className={({ isActive }) =>
                cn(
                  'p-2 text-white/80 hover:text-white transition-colors rounded-lg hover:bg-white/20',
                  isActive && 'text-cyan-300 bg-cyan-500/30 shadow-md'
                )
              }
              title="Reports"
            >
              <FileText size={20} />
            </NavLink>

            <NavLink
              to="/audit"
              className={({ isActive }) =>
                cn(
                  'p-2 text-white/80 hover:text-white transition-colors rounded-lg hover:bg-white/20',
                  isActive && 'text-cyan-300 bg-cyan-500/30 shadow-md'
                )
              }
              title="Audit Trail"
            >
              <ScrollText size={20} />
            </NavLink>

            <NavLink
              to="/settings"
              className={({ isActive }) =>
                cn(
                  'p-2 text-white/80 hover:text-white transition-colors rounded-lg hover:bg-white/20',
                  isActive && 'text-cyan-300 bg-cyan-500/30 shadow-md'
                )
              }
              title="Settings"
            >
              <Settings size={20} />
            </NavLink>

            {/* Divider */}
            <div className="h-6 w-px bg-white/20 mx-2"></div>

            {/* User Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowUserDropdown(!showUserDropdown)}
                className="flex items-center space-x-3 p-2 rounded-lg hover:bg-white/20 transition-colors"
              >
                <div className="flex items-center justify-center w-8 h-8 bg-cyan-500/30 rounded-full border-2 border-cyan-400/50">
                  <User size={16} className="text-cyan-300" />
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-medium text-white">
                    {user.firstName} {user.lastName}
                  </p>
                  <p className="text-xs text-neutral-400">
                    {getRoleDisplayName(user.role)}
                  </p>
                </div>
                <ChevronDown size={16} className="text-neutral-400" />
              </button>

              {showUserDropdown && (
                <div className="absolute right-0 mt-2 w-64 bg-neutral-900 border border-neutral-700 rounded-lg shadow-lg z-50">
                  <div className="p-4 border-b border-neutral-700">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center justify-center w-10 h-10 bg-primary-500/20 rounded-full border border-primary-500/30">
                        <User size={20} className="text-primary-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white">
                          {user.firstName} {user.lastName}
                        </p>
                        <p className="text-sm text-neutral-400">{user.email}</p>
                        <div className="mt-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                            {getRoleDisplayName(user.role)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 space-y-2">
                    <div className="text-sm">
                      <p className="text-neutral-400">Organization</p>
                      <p className="text-white">{user.organization}</p>
                    </div>
                    <div className="text-sm">
                      <p className="text-neutral-400">Department</p>
                      <p className="text-white">{user.department}</p>
                    </div>
                    <div className="text-sm">
                      <p className="text-neutral-400">Last Login</p>
                      <p className="text-white">
                        {user.lastLogin?.toLocaleDateString()} at {user.lastLogin?.toLocaleTimeString()}
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-neutral-700 p-2">
                    <button className="w-full text-left px-3 py-2 text-sm text-neutral-300 hover:text-white hover:bg-neutral-800 rounded">
                      Profile Settings
                    </button>
                    <button className="w-full text-left px-3 py-2 text-sm text-neutral-300 hover:text-white hover:bg-neutral-800 rounded">
                      Security
                    </button>
                    <button className="w-full text-left px-3 py-2 text-sm text-danger-400 hover:text-danger-300 hover:bg-neutral-800 rounded">
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}